import Quill from 'quill';
import { DATA_LINK_DESC_ATTR } from './quill-link-placeholder';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Embed = Quill.import('blots/embed') as any;

export const DATA_TAG_BLOT_NAME = 'dataTag';
export const DATA_TAG_EMPTY_CLASS = 'ql-data-tag--empty';

type LinkMeta = { label: string; description?: string };

let metaByTag = new Map<string, LinkMeta>();
let resolveLabel: (tag: string) => string = tag => tag;
let resolveHint: (tag: string) => string | undefined = () => undefined;
let resolveFilled: (tag: string) => boolean = () => true;

export function setDataTagResolvers(
    label: (tag: string) => string,
    hint?: (tag: string) => string | undefined
): void {
    resolveLabel = label;
    resolveHint = hint ?? (() => undefined);
}

export function setDataTagFilledResolver(filled: (tag: string) => boolean): void {
    resolveFilled = filled;
}

export function setDataTagMetadata(
    links: Array<{ value?: string; name?: string; description?: string }>
): void {
    metaByTag = new Map();
    for (const link of links) {
        const tag = (link.value ?? link.name ?? '').replace(/^\$/, '').trim();
        if (!tag) continue;
        metaByTag.set(tag, {
            label: resolveLabel(tag),
            description: resolveHint(tag) ?? link.description,
        });
    }
}

/** Metadados para tags presentes no texto mas ausentes na lista da API. */
export function ensureDataTagMeta(tag: string): void {
    const key = tag.replace(/^\$/, '').trim();
    if (!key || metaByTag.has(key)) return;
    metaByTag.set(key, {
        label: resolveLabel(key),
        description: resolveHint(key),
    });
}

function applyFilledClass(node: HTMLElement, tag: string): void {
    const filled = resolveFilled(tag);
    node.classList.toggle(DATA_TAG_EMPTY_CLASS, !filled);
}

export function updateDataTagValidityClasses(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>(`.${DataTagBlot.className}[data-tag]`).forEach(node => {
        const tag = node.getAttribute('data-tag') ?? '';
        if (tag) applyFilledClass(node, tag);
    });
}

export function getDataTagFromInsert(insert: object): string | null {
    const tag = (insert as Record<string, unknown>)[DATA_TAG_BLOT_NAME];
    return typeof tag === 'string' && tag ? tag : null;
}

export function isDataTagInsert(insert: unknown): insert is Record<string, string> {
    return typeof insert === 'object' && insert !== null && getDataTagFromInsert(insert) != null;
}

/**
 * Blot embed para tags de dados ({ insert: { dataTag: "link_client_document" } }).
 */
class DataTagBlot extends Embed {
    static blotName = DATA_TAG_BLOT_NAME;
    static tagName = 'span';
    static className = 'ql-data-tag';

    static create(value: string) {
        const node = super.create() as HTMLSpanElement;
        const tag = (value ?? '').replace(/^\$/, '').trim();
        node.setAttribute('data-tag', tag);
        // Não marcar o nó raiz como contenteditable=false: a base Embed do Quill
        // já isola o conteúdo num contentNode não-editável e mantém "guards"
        // (text nodes editáveis) em volta. Marcar a raiz inteira quebra os guards
        // e o cursor "some" ao clicar ao lado da tag.
        ensureDataTagMeta(tag);
        const meta = metaByTag.get(tag);
        node.textContent = meta?.label ?? resolveLabel(tag);
        const hint = meta?.description ?? resolveHint(tag);
        if (hint) node.setAttribute(DATA_LINK_DESC_ATTR, hint);
        node.classList.add(DataTagBlot.className);
        applyFilledClass(node, tag);
        return node;
    }

    static value(domNode: HTMLElement) {
        return domNode.getAttribute('data-tag') ?? '';
    }
}

export function registerDataTagBlot(): void {
    try {
        Quill.register(DataTagBlot);
    } catch {
        // Já registrado
    }
}
